import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { workspacePickersAsUsers } from '@/api/services/workspaceUsers';
import type { SearchAclEntry, User } from '@/types';

const ALL_PERMISSIONS = ['view', 'edit', 'run', 'manage'] as const;
type Permission = (typeof ALL_PERMISSIONS)[number];

type Props = {
  searchId: string;
  searchOwnerId?: string | null;
  currentUserId?: string | null;
  canManageAcl: boolean;
};

type PrincipalAcl = {
  principalId: string;
  principalName?: string | null;
  principalEmail?: string | null;
  permissions: Set<Permission>;
};

function displayUser(user: User): string {
  return user.name?.trim() || user.email || user.id;
}

function groupAclEntries(entries: SearchAclEntry[]): PrincipalAcl[] {
  const byPrincipal = new Map<string, PrincipalAcl>();
  for (const entry of entries) {
    const existing = byPrincipal.get(entry.principalId) ?? {
      principalId: entry.principalId,
      principalName: entry.principalName,
      principalEmail: entry.principalEmail,
      permissions: new Set<Permission>(),
    };
    if (ALL_PERMISSIONS.includes(entry.permission as Permission)) {
      existing.permissions.add(entry.permission as Permission);
    }
    byPrincipal.set(entry.principalId, existing);
  }
  return Array.from(byPrincipal.values());
}

export default function SearchAclPanel({
  searchId,
  searchOwnerId,
  currentUserId,
  canManageAcl,
}: Props) {
  const [entries, setEntries] = useState<SearchAclEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [principalId, setPrincipalId] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<Permission>>(
    () => new Set<Permission>(['view']),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const principals = useMemo(() => groupAclEntries(entries), [entries]);

  const load = useCallback(async () => {
    if (!canManageAcl) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [aclRows, pickers] = await Promise.all([
        api.searches.listAcl(searchId),
        api.workspaceUsers.pickers(),
      ]);
      setEntries(aclRows);
      const existingIds = new Set(aclRows.map((row) => row.principalId));
      setUsers(
        workspacePickersAsUsers(pickers.items).filter(
          (u) =>
            u.id !== searchOwnerId &&
            u.id !== currentUserId &&
            u.is_enabled !== false &&
            !existingIds.has(u.id),
        ),
      );
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load access rules');
    } finally {
      setLoading(false);
    }
  }, [canManageAcl, currentUserId, searchId, searchOwnerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canManageAcl) {
    return null;
  }

  function togglePermission(perm: Permission) {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) {
        next.delete(perm);
      } else {
        next.add(perm);
      }
      return next;
    });
  }

  async function savePrincipal(e: React.FormEvent) {
    e.preventDefault();
    if (!principalId || selectedPermissions.size === 0) return;
    setSaving(true);
    setError('');
    try {
      await api.searches.setAcl(searchId, {
        principalType: 'user',
        principalId,
        permissions: Array.from(selectedPermissions),
      });
      setPrincipalId('');
      setSelectedPermissions(new Set<Permission>(['view']));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save access');
    } finally {
      setSaving(false);
    }
  }

  async function removePrincipal(targetId: string) {
    setSaving(true);
    setError('');
    try {
      await api.searches.deleteAcl(searchId, targetId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove access');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="search-share-panel">
      <h4 className="search-share-panel__title">Access control (ACL)</h4>
      <p className="muted search-share-panel__hint">
        Grant view, edit, run, or manage permissions to organization members without transferring ownership.
      </p>
      {error ? <div className="error">{error}</div> : null}
      {loading ? <p className="muted">Loading access rules…</p> : null}
      {!loading && principals.length > 0 ? (
        <ul className="search-share-panel__list">
          {principals.map((principal) => (
            <li key={principal.principalId}>
              <span>
                {principal.principalName || principal.principalEmail || principal.principalId}
              </span>
              <span className="muted">{Array.from(principal.permissions).sort().join(' · ')}</span>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={saving}
                onClick={() => void removePrincipal(principal.principalId)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!loading ? (
        <form className="search-share-panel__form" onSubmit={(e) => void savePrincipal(e)}>
          <label htmlFor="acl-principal">Team member</label>
          <select
            id="acl-principal"
            className="filter-select"
            value={principalId}
            onChange={(e) => setPrincipalId(e.target.value)}
          >
            <option value="">Select user…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {displayUser(u)} ({u.role})
              </option>
            ))}
          </select>
          {ALL_PERMISSIONS.map((perm) => (
            <label key={perm} className="checkbox-inline">
              <input
                type="checkbox"
                checked={selectedPermissions.has(perm)}
                onChange={() => togglePermission(perm)}
              />
              {perm}
            </label>
          ))}
          <button
            type="submit"
            className="btn btn-sm"
            disabled={saving || !principalId || selectedPermissions.size === 0}
          >
            {saving ? 'Saving…' : 'Grant access'}
          </button>
        </form>
      ) : null}
    </div>
  );
}
